<template>
	<div class="c-table">

		<div class="flex flex-space-between" v-if="search">
			
			<div>
				<c-search :placeholder="placeholder" @search="applySearch"></c-search>
			</div>
			
			<div class="flex">
				<div v-for="filter in filters" class="margin-l-8">
					<c-filter v-if="filter.options && filter.options.length > 2"
						:name="filter.name"
						:placeholder="filter.label"
						:options="filter.options"
						:filterValues="selection.filters"
						@update:filterValues="applyFilters"> 
					</c-filter>
				</div>
			</div>
		</div>

		<div class="c-table c-table--responsive" :class="{ 'c-table--checkbox': checkbox, 'c-table--small': small, 'c-table--resizable': resizable }">
			<table class="c-table__element">
				<thead>
					<tr>
						<template v-for="column in columns" :key="column.name">
							<th
								v-if="column.sortable"
								@click.prevent="!isResizing && applySort(column.sortField ?? column.name)"
								:class="[
									{ 'c-table__column--sorted-asc': column.name == sort.column && sort.direction == 'asc' },
									{ 'c-table__column--sorted-desc': column.name == sort.column && sort.direction == 'desc' },
									{ 'c-table__column--sortable': column.sortable == true }
								]"
								:style="{ width: column.width ? column.width + 'px' : undefined }"
								ref="tableHeaders"
							>
								<a href="#" class="c-table__column">
									{{ column.label }}
								</a>
								<span v-if="resizable" class="resizer" @mousedown.stop="initResize($event, $event.target)"></span>
							</th>
							<th v-else :style="{ width: column.width ? column.width + 'px' : undefined }">
								<a href="#" class="c-table__column">
									{{ column.label }}
								</a>
								<span v-if="resizable" class="resizer" @mousedown.stop="initResize($event, $event.target)"></span>
							</th>
						</template>
					</tr>
				</thead>
				<tbody>
					<tr v-if="loading">
						<td :colspan="columns.length">
							<c-loader-container :loading="true"></c-loader-container>
						</td>
					</tr>
					<tr v-else-if="rows.length == 0">
						<td :colspan="columns.length" class="text-center">Er is geen data gevonden</td>
					</tr>
					<tr v-else v-for="row in rows" :key="row.column">
						<td v-for="(column) in columns" :key="column.name">
							<component :is="column.component || 'CColumnText'" v-bind="column.props"
								:value="getValueByName(row, column.name)">
							</component>
						</td>
					</tr>
				</tbody>
				
			</table>
		</div>

		<!-- <c-pagination></c-pagination> -->

		<div class="flex c-pagination text-center">
			<c-pagination v-if="!loading && page.total > 1" :links="page.links" @selectPage="setPage"></c-pagination>

			<p v-if="loadMore && !loading && !showAllData" class="color-primary cursor-pointer margin-t-4" @click="showAll()">
				Laad meer
			</p>
		</div>
	</div>
</template>

<script>
import CSearch from '@components/table/Search';
import CFilter from '@components/table/Filter';
import CLoaderContainer from '@components/loader/Container';
import CPagination from '@components/table/Pagination';
import CColumnButton from '@components/table/column/Button';
import CColumnCheckbox from '@components/table/column/Checkbox';
import CColumnText from '@components/table/column/Text';
import CColumnDatetime from '@components/table/column/Datetime';
import CColumnTime from '@components/table/column/Time';
import CColumnDateWithHours from '@components/table/column/DateWithHours';
import CColumnChangePrice from '@components/table/column/ChangePrice';
import CColumnPrice from '@components/table/column/Price';
import CColumnActiveproduct from '@components/table/column/ActivateProduct';

export default {
	components: { CSearch, CFilter, CColumnActiveproduct, CLoaderContainer, CPagination, CColumnButton, CColumnCheckbox, CColumnText, CColumnDatetime, CColumnTime, CColumnDateWithHours, CColumnChangePrice, CColumnPrice },
	props: {
		columns: {
			type: Array,
			required: true
		},
		search: {
			type: Boolean,
			default: true
		},
		checkbox: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: 'Zoeken op aanvraag, nummer, status…'
		},
		endpoint: {
			type: String,
			required: false
		},
		testRows: {
			type: Array,
			required: false
		},
		small: {
			type: Boolean,
			default: false
		},
		filters: {
			type: Array,
			default: () => []
		},
		filterValues: {
			type: Object,
			default: () => ({})
		},
		resizable: {
			type: Boolean,
			default: true
		},
		loadMore: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			page: {
				links: [],
				total: 0,
			},
			selection: {
				search: {
					all: '',
				},
				filters: {},
			},
			applied: {
				search: ''
			},
			showAllData: false,
			loading: false,
			rows: [],
			searchValue: '',
			sort: {
				direction: 'asc',
				column: '',
			},
			dataUrl: '',
			isResizing: false,
		}
	},
	methods: {
		applySearch(value) {
			this.selection.search.all = value;
		},
		applyFilters(filterValues, name) {
			this.selection.filters[name] = filterValues;
		},
		applySort(column) {
			this.sort.direction = this.sort.column == column && this.sort.direction == 'asc' ? 'desc' : 'asc';
			this.sort.column = column;
		},
		setPage(url) {
			this.dataUrl = url;
		},
		showAll() {
			this.showAllData = true
			this.load()
		},
		getValueByName(row, keys) {
			return keys
				.split(',')
				.map(key => key.trim())
				.map(key => (function reducer(data, keyParts) {
					let keyPart;
					let localData = data;
					while(keyPart = keyParts.shift()) {
						if (localData instanceof Array && keyPart == '*') {
							return localData.map(d => reducer(d, [...keyParts])).join(', ');
						} else if (localData instanceof Object && keyPart in localData) {
							localData = localData[keyPart];
						} else {
							return null;
						}
					}
					return localData;
				})(row, key.split('.')) ?? '')
				.join(' ');
		},

		load() {
			const endpoint = this.dataUrl;
			let params = {
				filter: {},
			};
			if (this.sort) {
				params.sort = this.sort;
			}

			if(this.showAll && this.showAllData){
				params.showAll = true;
			}
			
			if (this.selection && this.selection.filters) {
				params.filter = Object.assign(params.filter, this.selection.filters);
			}
			Object.assign(params.filter, this.selection.search);

			if (endpoint) {
				this.loading = true;
				this.$xhr.get(endpoint, params).then(({ result }) => {
					this.rows = result.data ?? result;
					this.page.total = result.last_page ?? 1;
					this.page.links = result.links ?? [{ url: endpoint, active: true, labeL: 1 }];
					this.loading = false;
				}).catch((e) => {
					this.loading = false;
					this.rows = [];
					this.$store.dispatch('toast/alert', {title: 'Foutmelding', text: 'Er kan geen data geladen worden'});
				});
			}
			else{
				this.rows = this.testRows;
			}
		},

		initResize(e, resizer) {
			const th = resizer.parentElement;
			const startX = e.clientX;
			const startWidth = th.offsetWidth;

			this.isResizing = true;

			const onMouseMove = (e) => {
				const newWidth = startWidth + (e.clientX - startX);
				th.style.width = newWidth + 'px';
			};

			const onMouseUp = () => {
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
				resizer.classList.remove('resizing');

				setTimeout(() => {
					this.isResizing = false;
				}, 50);
			};

			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
			resizer.classList.add('resizing');
		}
	},
	watch: {
		endpoint(newUrl, oldUrl) { this.dataUrl = newUrl; },
		dataUrl(v) { this.load() },
		testRows: {deep: true, handler(newRows) { this.rows = newRows; }, },
		sort: { deep: true, handler() { this.load() } },
		selection: { deep: true, handler() { this.load() } },
	},
	beforeMount() {
		this.dataUrl = this.endpoint;
	}
}
</script>